import React, { useEffect } from 'react'
import { HStack } from '@musinsa/mwms-ds'
import { Radio } from '@/components/content/Radio'
import Button from '@/components/content/Button'
import Input from '@/components/content/Input'
import SelectBox from '@/components/content/SelectBox'
import RadioGroup from '@/components/content/RadioGroup'
import { FormField } from '@/components/content/FormField'
import RemixIcon from '@/components/content/RemixIcon'
import styled from 'styled-components'
import Text from '@/components/content/Text'
import { colors, colorSystem, fontSystem } from '@/styles/theme'
import { UseFormReturn, Controller, useFieldArray } from 'react-hook-form'
import { ShipperForm } from '@/services/shipper/schemas'
import { PlatformType, ShipperItem } from '@/services/shipper/types'

interface Props {
  form: UseFormReturn<ShipperForm>
  type: 'create' | 'update'
}

interface PlatformOption {
  type: PlatformType
  value: string
  label: string
}

const PLATFORM_OPTIONS = [
  { type: 'MANAGED', value: '무신사', label: '무신사' },
  { type: 'MANAGED', value: '29CM', label: '29cm' },
  { type: 'OWN_MALL', value: 'OWN_MALL', label: '자사몰' },
  { type: 'ETC', value: 'ETC', label: '기타' },
] satisfies PlatformOption[]

const checkDuplicatedPlatform = (platforms: ShipperItem['salesPlatforms']) => {
  const platformMap = new Map<string, number>()

  for (let i = 0; i < platforms.length; i++) {
    const platform = platforms[i]
    if (platform.type === 'MANAGED') {
      if (platformMap.has(platform.name)) {
        return true
      }
      platformMap.set(platform.name, i)
    } else {
      if (platformMap.has(platform.type)) {
        return true
      }
      platformMap.set(platform.type, i)
    }
  }
}

function MultiplePlatformSection({ form, type }: Props) {
  const { control, watch, setValue, trigger } = form

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'salesPlatforms',
  })
  useEffect(() => {
    if (type === 'update') {
      trigger()
    }
  }, [])

  const watchedPlatforms = watch('salesPlatforms')
  const watchedShipperType = watch('logisticsServiceType')

  const hasDuplicatedPlatform = !!watchedPlatforms && checkDuplicatedPlatform(watchedPlatforms as ShipperItem['salesPlatforms'])

  // 한 개도 설정되지 않은 경우에만 validation 진행
  const hasAnyPlatform = watchedPlatforms?.some((platform) => platform?.type && platform?.companyId)

  const addPlatform = () => {
    if (fields.length < 4) {
      append({
        type: '',
        name: '',
        companyId: '',
        usable: true,
      })
    }
  }

  const removePlatform = (index: number) => {
    remove(index)
  }

  const getAvailablePlatformOptions = (index: number) => {
    // 현재 선택된 플랫폼들의 정보를 가져옴 (현재 인덱스 제외)
    const selectedPlatforms = watchedPlatforms?.filter((_, idx) => idx !== index)?.filter((platform) => platform?.type)

    // 현재 인덱스의 선택된 값
    const currentPlatform = watchedPlatforms?.[index]
    const currentPlatformValue = (() => {
      if (currentPlatform?.type === 'MANAGED') {
        return currentPlatform?.name
      }
      return currentPlatform?.type
    })()

    // 이미 선택된 플랫폼들을 제외한 옵션들만 반환 (단, 현재 선택된 값은 항상 포함)
    return PLATFORM_OPTIONS.filter((option) => {
      // 현재 선택된 값이면 항상 포함
      if (currentPlatformValue && option.value === currentPlatformValue) {
        return true
      }

      // 다른 곳에서 이미 선택된 값이면 제외
      return !selectedPlatforms?.some((selectedPlatform) => {
        if (selectedPlatform.type === 'MANAGED') {
          return selectedPlatform.name === option.value
        }

        return selectedPlatform.type === option.type
      })
    })
  }

  const handlePlatformChange = (index: number, selectedValue: string) => {
    const platformOption = PLATFORM_OPTIONS.find((option) => option.value === selectedValue)

    if (platformOption) {
      setValue(`salesPlatforms.${index}.type`, platformOption.type)

      if (platformOption.type === 'MANAGED') {
        setValue(`salesPlatforms.${index}.name`, platformOption.value)
      } else {
        setValue(`salesPlatforms.${index}.name`, '')
      }
    }

    trigger(`salesPlatforms.${index}.type`)
  }

  // 4개 모두 추가되었거나 사용 가능한 플랫폼이 없으면 추가 버튼 숨김
  const shouldShowAddButton = fields.length < 4 && fields.length < PLATFORM_OPTIONS.length
  const showOrderCollectionField = watchedShipperType === 'MWP'

  return (
    <PlatformSection>
      {/* 플랫폼 리스트 */}
      {fields.map((field, index) => {
        const currentPlatform = watchedPlatforms?.[index]
        const availableOptions = getAvailablePlatformOptions(index)
        const canDelete = fields.length > 1 || index > 0

        return (
          <PlatformItem key={`${currentPlatform?.type || 'empty'}_${index}`}>
            <PlatformHeader justify="space-between">
              <Text kind="Body_14_medium">판매 플랫폼 {index + 1}</Text>
              {canDelete && (
                <Button size="sm" fit kind="secondary" onClick={() => removePlatform(index)}>
                  <Text kind="Body_14_medium" color={colorSystem.text.common.primary}>
                    삭제
                  </Text>
                </Button>
              )}
            </PlatformHeader>

            <PlatformContent>
              <FormField label="플랫폼" required>
                <Controller
                  name={`salesPlatforms.${index}.type`}
                  control={control}
                  render={({ field, fieldState }) => {
                    const currentPlatformValue = (() => {
                      if (currentPlatform?.type === 'MANAGED') {
                        return currentPlatform?.name
                      }
                      return currentPlatform?.type
                    })()

                    return (
                      <>
                        <SelectBox
                          placeholder="선택"
                          value={currentPlatformValue || undefined}
                          options={availableOptions}
                          onChange={(value) => handlePlatformChange(index, value)}
                        />
                        {fieldState.error && (
                          <ErrorMessage kind="Body_12_medium" color={colorSystem.text.common.error}>
                            {fieldState.error.message}
                          </ErrorMessage>
                        )}
                      </>
                    )
                  }}
                />
              </FormField>

              {currentPlatform?.type === 'MANAGED' && (
                <FormField label="업체아이디" required>
                  <Controller
                    name={`salesPlatforms.${index}.companyId`}
                    control={control}
                    rules={{
                      required: !hasAnyPlatform && currentPlatform?.type ? '올바른 업체아이디를 입력해 주세요.' : false,
                    }}
                    render={({ field, fieldState }) => (
                      <>
                        <Input {...field} placeholder="업체아이디 입력" value={field.value || ''} />
                        {fieldState.error && (
                          <ErrorMessage kind="Body_12_medium" color={colorSystem.text.common.error}>
                            {fieldState.error.message}
                          </ErrorMessage>
                        )}
                      </>
                    )}
                  />
                </FormField>
              )}

              {showOrderCollectionField && currentPlatform?.type === 'MANAGED' && (
                <FormField label="주문수집 여부" required>
                  <Controller
                    name={`salesPlatforms.${index}.usable`}
                    control={control}
                    render={({ field }) => (
                      <RadioGroup direction="row">
                        <Radio value="true" checked={field.value === true} onChange={() => field.onChange(true)} label="Y" />
                        <Radio value="false" checked={field.value === false} onChange={() => field.onChange(false)} label="N" />
                      </RadioGroup>
                    )}
                  />
                </FormField>
              )}

              {/* 플랫폼명 필드 (기타 선택 시에만) */}
              {(currentPlatform?.type === 'ETC' || currentPlatform?.type === 'OWN_MALL') && (
                <FormField label="플랫폼명">
                  <Controller
                    name={`salesPlatforms.${index}.name`}
                    control={control}
                    render={({ field }) => <Input {...field} placeholder="몰 이름 모두 입력(로 구분)" value={field.value || ''} />}
                  />
                </FormField>
              )}
            </PlatformContent>
          </PlatformItem>
        )
      })}
      {/* 플랫폼 추가 버튼 */}
      {shouldShowAddButton && (
        <Button fit kind="secondary" onClick={addPlatform}>
          <RemixIcon name="ri-add-line" size={16} />
          <Text kind="Body_14_medium" color={colorSystem.text.common.primary}>
            플랫폼 추가
          </Text>
        </Button>
      )}
    </PlatformSection>
  )
}

export default MultiplePlatformSection

const PlatformSection = styled.div`
  margin-top: 24px;
`

const PlatformItem = styled.div`
  margin-bottom: 24px;
`

const DeleteButton = styled.button`
  ${fontSystem.Body_14_medium};
  color: ${colors.gray800};

  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 16px;
  border-radius: 6px;
  border: 1px solid ${colors.gray300};

  cursor: pointer;
`
const PlatformHeader = styled(HStack)`
  margin-bottom: 12px;
`

const PlatformContent = styled.div``

const ErrorMessage = styled(Text)`
  margin-top: 2px;
  margin-left: 4px;
`
